import { Component, OnInit } from '@angular/core';
import { CustomFields } from 'features/custom-fields/custom-fields.interface';
import { ActivatedRoute } from '@angular/router';
import { CUSTOM_FIELDS_TYPES, trackCustomFieldByFn } from '../../custom-fields.constants';
import { appendFiles } from 'utils/formdata-builder';
import { InstanceCustomFieldsService } from '../instance-custom-fields.service';
import { PermissionsService } from 'services/permissions/permissions.service';
import { MessageQueryService } from '@certemy/ui-components';
import { Store } from '@ngrx/store';
import { Selectors as ProfileSelectors } from 'features/profiles-state/+state/profiles-state.state';
import { ROLE_CODES } from 'constants/role.constants';
import { take } from 'rxjs/operators';
import { IPermissions, PolicyPermissionsService } from 'services/policy-permission/policy-permissions.service';
import { PolicyObj } from 'services/policy-permission/policy-permissions.service';
import { CUSTOM_FIELD_TYPES } from 'constants/constants';
import { PublicGlobalService } from 'services/public-global/public-global.service';
import { Country, State } from 'interfaces/public-global.interface';
import { PERMISSIONS } from 'services/permissions/permissions.constants';

interface InstanceCustomFieldsPolicyPermissions extends IPermissions {
  editSettingsUsersCustomFields: boolean;
  editOwnProfileCustomFields: boolean;
}

const INSTANCE_CUSTOM_FIELDS_POLICIES: Record<string, PolicyObj> = {
  editSettingsUsersCustomFields: {
    name: 'editSettingsUsersCustomFields',
    url: 'settings/users/custom_fields',
    action: 'edit',
  },
  editOwnProfileCustomFields: { name: 'editOwnProfileCustomFields', url: 'settings/users/own_profile', action: 'edit' },
};

const CHANGES_SAVED_MESSAGE = 'The changes have been saved.';

@Component({
  selector: 'cm-instance-custom-fields',
  templateUrl: './instance-custom-fields.component.html',
  styleUrls: ['./instance-custom-fields.component.scss'],
})
export class InstanceCustomFieldsComponent implements OnInit {
  CUSTOM_FIELDS_TYPES = CUSTOM_FIELDS_TYPES;
  fields: CustomFields.ItemInstance[] = [];
  trackCustomFieldByFn = trackCustomFieldByFn;
  private isProfessional: boolean;
  headerTitle: string;
  policyPermissions: InstanceCustomFieldsPolicyPermissions = {
    editSettingsUsersCustomFields: false,
    editOwnProfileCustomFields: false,
  };
  policyObjectUrl: string;
  stateOptions: State[];
  countryOptions: Country[];

  readonly CUSTOM_FIELD_TYPES = CUSTOM_FIELD_TYPES;

  constructor(
    private route: ActivatedRoute,
    private instanceCustomFieldsService: InstanceCustomFieldsService,
    private messageQueryService: MessageQueryService,
    private store: Store<any>,
    private permissionsService: PermissionsService,
    private policyPermissionsService: PolicyPermissionsService,
    private publicGlobalService: PublicGlobalService,
  ) {}

  ngOnInit() {
    this.fields = this.route.snapshot.data.fields;
    this.policyObjectUrl = this.route.snapshot.data.policyObj;
    this.publicGlobalService
      .getStates(false)
      .pipe(take(1))
      .subscribe((data) => (this.stateOptions = data));
    this.publicGlobalService
      .getCountries()
      .pipe(take(1))
      .subscribe((data) => (this.countryOptions = data));

    this.store
      .select(ProfileSelectors.currentRoleId)
      .pipe(take(1))
      .subscribe((roleId: ROLE_CODES) => {
        this.isProfessional = roleId === ROLE_CODES.PROFESSIONAL;
        this.headerTitle = this.isProfessional ? 'Profile Fields' : 'Organizational Custom Fields';
      });

    this.policyPermissionsService
      .hasMultiplePermissions(Object.values(INSTANCE_CUSTOM_FIELDS_POLICIES))
      .pipe(take(1))
      .subscribe((permissions: InstanceCustomFieldsPolicyPermissions) => {
        this.policyPermissions = permissions;
      });
  }

  onValueChange(oldField: CustomFields.FieldInstance, newValue: any) {
    const newField = { ...oldField, value: newValue };
    this.instanceCustomFieldsService.updateUserCustomField(newField).subscribe(
      (res) => {
        this.fields = this.updateItems(this.fields, { ...oldField, value: res.value });
        this.messageQueryService.addMessage(CHANGES_SAVED_MESSAGE);
      },
      () => (this.fields = this.updateItems(this.fields, { ...oldField })),
    );
  }

  onAttachmentUpload(field: CustomFields.FieldInstance, files: File[]) {
    const data = appendFiles(new FormData(), files, 'files');
    this.instanceCustomFieldsService.uploadAttachmentForCustomField(field.value_id, data).subscribe(
      (attachments) => {
        this.fields = this.updateItems(this.fields, { ...field, value: attachments });
        this.messageQueryService.addMessage(CHANGES_SAVED_MESSAGE);
      },
      () => (this.fields = this.updateItems(this.fields, field)),
    );
  }

  onAttachmentRemove(field: CustomFields.FieldInstance, fileToRemove: CustomFields.Attachment) {
    this.instanceCustomFieldsService.removeAttachmentForCustomField(field.value_id, fileToRemove.id).subscribe(
      () =>
        (this.fields = this.updateItems(this.fields, {
          ...field,
          value: field.value.filter((file) => file.id !== fileToRemove.id),
        })),
      () => (this.fields = this.updateItems(this.fields, field)),
    );
  }

  private compareField = (el1) => (el2) => el1.field_id === el2.field_id;
  private compareGroup = (el1) => (el2) => el1.group_id === el2.group_id;

  private updateItems(items: CustomFields.ItemInstance[], newField: CustomFields.FieldInstance) {
    if (newField.group_id) {
      const fieldGroup = items.find(this.compareGroup(newField)) as CustomFields.GroupInstance;
      const newItem = this.updateGroup(fieldGroup, newField);
      return this.updateList(items, newItem, this.compareGroup);
    } else {
      return this.updateList(items, newField, this.compareField);
    }
  }

  private updateList<T>(list: T[], newElement: T, comparator: (el: T) => (el: T) => boolean): T[] {
    const elementInd = list.findIndex(comparator(newElement));
    return [...list.slice(0, elementInd), newElement, ...list.slice(elementInd + 1)];
  }

  private updateGroup(
    group: CustomFields.GroupInstance,
    newField: CustomFields.FieldInstance,
  ): CustomFields.GroupInstance {
    return { ...group, fields: this.updateList(group.fields, newField, this.compareField) };
  }

  isCustomFieldReadonly(customField: CustomFields.FieldInstance): boolean {
    if (
      (!this.policyPermissions.editSettingsUsersCustomFields &&
        this.policyObjectUrl === INSTANCE_CUSTOM_FIELDS_POLICIES.editSettingsUsersCustomFields.url) ||
      (!this.policyPermissions.editOwnProfileCustomFields &&
        this.policyObjectUrl === INSTANCE_CUSTOM_FIELDS_POLICIES.editOwnProfileCustomFields.url)
    ) {
      return true;
    }

    const hasEditPermission = this.permissionsService.hasPermissions(PERMISSIONS.customFieldInstanceEdit);

    return !hasEditPermission || (customField.professional_read_only && this.isProfessional);
  }
}
